// ============================================================================
// client/parcours-carte.ts — L'ARCHIPEL DU PARCOURS
// ----------------------------------------------------------------------------
// Choisie par Claude-Alain le 31/07 (piste « carte au trésor »), déclinée en B1+B2+B3 le 01/08,
// puis REPRISE le 01/08 sur son retour : « le parcours doit faire qu'un, tout doit être relié, et
// le tracé du chemin doit être varié et non identique ».
//
// ⚠️ CE FICHIER EST LA SEULE DÉFINITION du dessin ET de la géométrie. Le jeu l'appelle pour peindre
// la mer et pour POSER LES BOUTONS : deux calculs séparés finiraient par diverger, et les jetons
// repartiraient en pleine mer comme la première fois.
//
// ⚠️ TOUT SE CALCULE EN COORDONNÉES GLOBALES, y = 0 en haut du premier monde. Chaque monde garde
// son canvas — pour son en-tête, son défilement et ses boutons DOM, donc les clics, le focus
// clavier et les lecteurs d'écran — mais ce canvas n'est plus une carte : c'est une FENÊTRE sur LA
// carte, qu'il peint en entier, translatée. C'est cela, et cela seul, qui rend la couture
// invisible : deux fenêtres voisines repeignent les mêmes pixels à la jointure à partir des mêmes
// nombres. Une géométrie recalculée par bande décalerait d'un pixel à chaque monde, et la mer
// repartirait de zéro huit fois.
//
// ⚠️ HIÉRARCHIE, du plus fort au plus faible : le chemin, les jetons, les îles, le décor, la mer.
// ============================================================================

import { OMBRE_TON, papier, rgba, setDetail } from './papier'

type Ctx = CanvasRenderingContext2D
export interface Pt { x: number; y: number }

const bru = (i: number, j = 1): number => {
  const x = Math.sin(i * 127.1 + j * 311.7) * 43758.5453
  return x - Math.floor(x)
}

// ─────────────────────────────────────────────────────────────────────────────
// LES HUIT TRACÉS
// ---------------------------------------------------------------------------
// ⚠️ UN MONDE = UN TRACÉ, et deux mondes n'ont jamais le même. C'était la demande : le chemin
// répétait le même arc huit fois de suite, ce qui donnait huit fois la même image.
//
// Chaque tracé est une POLYLIGNE de repères en coordonnées normalisées : u dans [-1,1] de gauche
// à droite, v dans [-1,1] de haut en bas, l'origine au centre de l'île. Des repères plutôt qu'une
// formule parce qu'ici le dosage est un travail de dessin : on veut choisir où passe la marche,
// pas subir ce que sort une sinusoïde.
//
// ⚠️ DEUX RÈGLES QUE TOUT TRACÉ DOIT TENIR, sinon le parcours cesse de se lire comme un voyage :
//   · il ENTRE en haut (v ≤ -0,7) et SORT en bas (v ≥ +0,7) — la marche va toujours vers le bas,
//     donc d'une île à la suivante, jamais en remontant ;
//   · il entre et sort du CÔTÉ de l'île qui regarde le reste de l'archipel (u ≥ +0,2 ici, miroir
//     appliqué plus bas pour les îles de droite). Sans cela le ruban traverse l'île en diagonale
//     pour rejoindre la suivante, et on lit une croix au lieu d'un chemin.
// Entre ces deux points, le tracé fait ce qu'il veut : c'est là qu'est la variété.
// ─────────────────────────────────────────────────────────────────────────────
type Repere = readonly [number, number]
export const TRACES: readonly (readonly Repere[])[] = [
  // 0 · LE BOL — le chantier : on plonge vers la gauche, on longe le fond, on ressort à droite.
  [[0.55, -0.72], [-0.20, -0.90], [-0.80, -0.35], [-0.55, 0.45], [0.45, 0.70]],
  // 1 · LE SERPENTIN — glace et sable : un S serré, on glisse d'un bord à l'autre.
  [[0.62, -0.80], [-0.42, -0.40], [0.30, 0.15], [-0.28, 0.52], [0.20, 0.88]],
  // 2 · L'ESCALIER — l'usine : quatre marches à angle droit, comme une machine.
  [[0.40, -0.80], [0.40, -0.10], [-0.45, -0.10], [-0.45, 0.60], [0.30, 0.78]],
  // 3 · LA BOUCLE — les mirages : le chemin fait le tour complet de l'île avant de sortir.
  [[0.30, -0.85], [-0.75, -0.55], [-0.10, 0.60], [0.72, 0.05], [0.25, 0.82]],
  // 4 · L'ÉPINGLE — les serrures : on file tout à gauche, demi-tour, on revient.
  [[0.45, -0.80], [-0.55, -0.85], [-0.85, -0.25], [0.25, 0.15], [0.55, 0.75]],
  // 5 · LE SILLON — la grande ferme : de longues passes d'un bout à l'autre, comme un labour.
  [[0.62, -0.82], [-0.78, -0.55], [0.72, -0.15], [-0.72, 0.25], [0.35, 0.80]],
  // 6 · LES REMOUS — les profondeurs : on descend, un courant remonte, on replonge.
  [[0.40, -0.85], [0.55, 0.30], [-0.15, -0.55], [-0.70, 0.35], [0.42, 0.78]],
  // 7 · LA CRÊTE — le dernier étage : on longe la façade du manoir, puis on tombe.
  [[0.30, -0.88], [-0.30, -0.70], [0.60, -0.55], [-0.10, 0.10], [0.40, 0.72]],
  // 8 · LE HUIT — la foire aux farces : la boucle d'un carrousel. On file au large, on revient
  // frôler le centre, on repart : le seul tracé qui repasse deux fois du même côté.
  // ⚠️ QUATRE contraintes se tiennent ici en même temps, et elles se contredisent : assez
  // différent des huit autres (>0,4 d'écart moyen), assez ÉTALÉ pour que deux étapes voisines
  // gardent 50 px sur un téléphone de 430, et entrée comme sortie du bon côté de l'île.
  // Trois essais successifs ont chacun cassé une contrainte en réparant la précédente ; celui-ci
  // a été cherché contre la vraie fonction de disposition, pas à l'œil. Écart 0,690, marge 52 px.
  [[0.30, -0.86], [0.80, -0.45], [-0.15, -0.20], [0.72, 0.25], [0.25, 0.82]],
]

/** Les échelles du tracé dans l'île. `V` reste bien sous 0,72 (l'aplatissement des côtes) : une
 *  étape doit rester SUR la terre, jamais posée sur l'eau. */
const U = 0.82, V = 0.58

/** Échantillonne un tracé pour `n` étapes. À 5 (le cas de tous les mondes) les repères sont pris
 *  tels quels : le dosage dessiné à la main est conservé exactement. Au-delà, on répartit à
 *  longueur d'arc égale — un monde à 6 ou 7 étapes garde alors sa silhouette et son espacement. */
export function echantillonTrace(anc: readonly Repere[], n: number): Repere[] {
  if (n <= 0) return []
  if (n === 1) return [anc[0] ?? [0, 0]]
  if (n === anc.length) return anc.map((p) => [p[0], p[1]] as Repere)
  const cum = [0]
  for (let i = 1; i < anc.length; i++) {
    cum.push(cum[i - 1]! + Math.hypot(anc[i]![0] - anc[i - 1]![0], anc[i]![1] - anc[i - 1]![1]))
  }
  const tot = cum[cum.length - 1] || 1
  return Array.from({ length: n }, (_, k) => {
    const d = tot * k / (n - 1)
    let i = 1
    while (i < anc.length - 1 && cum[i]! < d) i++
    const a = anc[i - 1]!, b = anc[i]!, l = (cum[i]! - cum[i - 1]!) || 1
    const p = (d - cum[i - 1]!) / l
    return [a[0] + (b[0] - a[0]) * p, a[1] + (b[1] - a[1]) * p] as Repere
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// LA DISPOSITION — la géométrie de tout l'archipel, en pixels globaux.
// ─────────────────────────────────────────────────────────────────────────────
export interface Ile {
  /** Rang dans l'archipel (0 = la première île affichée). Décide du côté. */
  rang: number
  /** Index du VRAI monde (0-7) : décide du décor et du tracé, même si un monde vide est sauté. */
  decor: number
  cx: number; cy: number; r: number
  /** L'île est-elle posée à gauche de la mer ? Une sur deux, pour que l'archipel zigzague. */
  gauche: boolean
}
export interface Disposition {
  w: number
  hBande: number
  iles: Ile[]
  /** Les étapes de chaque île, en pixels globaux. */
  etapes: Pt[][]
  /** LE ruban : toutes les étapes de toutes les îles dans l'ordre, plus un point de haute mer
   *  entre deux îles. Un seul chemin, du premier pas au dernier boss. */
  ruban: Pt[]
}

/**
 * La géométrie de l'archipel entier. `mondes` donne, pour chaque monde AFFICHÉ, son nombre
 * d'étapes et son index de monde réel.
 * ⚠️ Fonction PURE : mêmes entrées, mêmes nombres. C'est ce qui garantit que deux bandes voisines
 * peignent la jointure au même endroit.
 */
export function dispositionParcours(
  mondes: readonly { n: number; decor: number }[], w: number, hBande: number,
): Disposition {
  const r = Math.min(w * 0.30, hBande * 0.52)
  // Le décalage latéral : assez pour que l'archipel zigzague et occupe la largeur, jamais assez
  // pour qu'une île sorte de la mer. Le troisième terme est la garde — sans lui, un écran étroit
  // rognait les côtes ; sans le deuxième, un écran large laissait l'archipel en colonne au milieu.
  const dx = Math.min(w * 0.26, r * 1.25, Math.max(0, w / 2 - r * 1.08))
  const iles: Ile[] = []
  const etapes: Pt[][] = []
  mondes.forEach((m, rang) => {
    const gauche = rang % 2 === 0
    const cx = w / 2 + (gauche ? -dx : dx)
    const cy = rang * hBande + hBande / 2
    iles.push({ rang, decor: m.decor, cx, cy, r, gauche })
    const anc = echantillonTrace(TRACES[m.decor % TRACES.length]!, m.n)
    // ⚠️ MIROIR sur les îles de droite : les tracés sont dessinés pour une île de GAUCHE, qui
    // entre et sort par sa droite. Une île de droite doit entrer et sortir par sa gauche, sinon
    // le ruban fait un aller-retour en travers de la mer entre chaque monde.
    etapes.push(anc.map(([u, v]) => ({ x: cx + (gauche ? u : -u) * r * U, y: cy + v * r * V })))
  })
  // LA HAUTE MER entre deux îles. Sans elle, la traversée est un trait tendu du bas d'une île au
  // haut de la suivante : une corde, pas une route maritime. Le point est poussé vers le large du
  // côté de l'île qu'on quitte, donc toujours en pleine eau, jamais sur une côte.
  const ruban: Pt[] = []
  etapes.forEach((etp, i) => {
    ruban.push(...etp)
    const suivant = etapes[i + 1]
    const sortie = etp.at(-1), entree = suivant?.[0]
    if (!sortie || !entree) return
    ruban.push({
      x: (sortie.x + entree.x) / 2 + (iles[i]!.gauche ? -1 : 1) * w * 0.09,
      y: (sortie.y + entree.y) / 2,
    })
  })
  return { w, hBande, iles, etapes, ruban }
}

// ─────────────────────────────────────────────────────────────────────────────
// LE DESSIN
// ─────────────────────────────────────────────────────────────────────────────

function contour(g: Ctx, cx: number, cy: number, r: number, gonfle: number, graine: number): void {
  const pts: Pt[] = []
  for (let k = 0; k < 24; k++) {
    const a = k / 24 * Math.PI * 2
    const rr = r * gonfle * (0.9 + 0.1 * Math.sin(k * 1.7 + graine * 0.05) + 0.05 * bru(k, graine))
    pts.push({ x: cx + Math.cos(a) * rr, y: cy + Math.sin(a) * rr * 0.72 })
  }
  g.beginPath()
  g.moveTo((pts[0]!.x + pts[23]!.x) / 2, (pts[0]!.y + pts[23]!.y) / 2)
  for (let k = 0; k < pts.length; k++) {
    const p = pts[k]!, q = pts[(k + 1) % pts.length]!
    g.quadraticCurveTo(p.x, p.y, (p.x + q.x) / 2, (p.y + q.y) / 2)
  }
  g.closePath()
}

/**
 * Une courbe qui PASSE PAR chaque point (Catmull-Rom centripète, α = 0,5).
 * ⚠️ Deux pièges évités ici, et c'est pour eux que la fonction existe :
 *   · un simple `lineTo` d'étape en étape donnait des angles cassés — un fil, pas un chemin ;
 *   · une courbe lissée « par les milieux » ne passe PAS par les points : les jetons se seraient
 *     retrouvés à côté de leur propre chemin.
 * Centripète et non uniforme, parce que la traversée entre deux îles est bien plus longue qu'un
 * pas d'une étape à l'autre : en uniforme, cette longueur emballe la tangente et la courbe part
 * en boucle au large.
 */
export function courbeDouce(g: Ctx, pts: readonly Pt[]): void {
  if (pts.length < 2) return
  g.beginPath()
  g.moveTo(pts[0]!.x, pts[0]!.y)
  if (pts.length === 2) { g.lineTo(pts[1]!.x, pts[1]!.y); return }
  const d = (a: Pt, b: Pt): number => Math.max(1e-4, Math.hypot(b.x - a.x, b.y - a.y) ** 0.5)
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]!, p1 = pts[i]!, p2 = pts[i + 1]!, p3 = pts[i + 2] ?? pts[i + 1]!
    const d1 = d(p0, p1), d2 = d(p1, p2), d3 = d(p2, p3)
    const c1x = (d1 * d1 * p2.x - d2 * d2 * p0.x + (2 * d1 * d1 + 3 * d1 * d2 + d2 * d2) * p1.x) / (3 * d1 * (d1 + d2))
    const c1y = (d1 * d1 * p2.y - d2 * d2 * p0.y + (2 * d1 * d1 + 3 * d1 * d2 + d2 * d2) * p1.y) / (3 * d1 * (d1 + d2))
    const c2x = (d3 * d3 * p1.x - d2 * d2 * p3.x + (2 * d3 * d3 + 3 * d3 * d2 + d2 * d2) * p2.x) / (3 * d3 * (d3 + d2))
    const c2y = (d3 * d3 * p1.y - d2 * d2 * p3.y + (2 * d3 * d3 + 3 * d3 * d2 + d2 * d2) * p2.y) / (3 * d3 * (d3 + d2))
    g.bezierCurveTo(c1x, c1y, c2x, c2y, p2.x, p2.y)
  }
}

/** Le décor propre à chaque monde, à l'intérieur des côtes. `iM` est l'index du monde (0-7). */
function decor(g: Ctx, cx: number, cy: number, r: number, iM: number): void {
  const e = r / 96                                          // échelle : le décor suit la taille de l'île
  g.save()
  contour(g, cx, cy, r, 0.82, cx); g.clip()
  if (iM === 0) {                                            // LE CHANTIER : échafaudage + gravats
    g.strokeStyle = rgba('#8a5a2a', 0.9); g.lineWidth = 3 * e
    for (const dx of [-34, -22]) { g.beginPath(); g.moveTo(cx + dx * e, cy - 30 * e); g.lineTo(cx + dx * e, cy + 18 * e); g.stroke() }
    for (const dy of [-22, -6, 10]) { g.beginPath(); g.moveTo(cx - 36 * e, cy + dy * e); g.lineTo(cx - 20 * e, cy + dy * e); g.stroke() }
    g.fillStyle = rgba('#a06a3a', 0.85)
    for (let k = 0; k < 5; k++) { g.beginPath(); g.arc(cx + (20 + bru(k, 2) * 30) * e, cy + (14 + bru(k, 5) * 10) * e, (4 + bru(k, 8) * 3) * e, 0, 7); g.fill() }
  } else if (iM === 1) {                                     // GLACE ET SABLE : sapins + banquise
    for (let k = 0; k < 4; k++) {
      const sx = cx + (-30 + k * 20) * e, sy = cy + (-8 + bru(k, 3) * 12) * e
      g.fillStyle = '#3f7a52'
      g.beginPath(); g.moveTo(sx, sy - 18 * e); g.lineTo(sx + 8 * e, sy); g.lineTo(sx - 8 * e, sy); g.closePath(); g.fill()
      g.fillStyle = '#5a3a20'; g.fillRect(sx - 1.6 * e, sy, 3.2 * e, 5 * e)
    }
    g.fillStyle = rgba('#ffffff', 0.7)
    for (let k = 0; k < 3; k++) { g.beginPath(); g.ellipse(cx + (-20 + k * 26) * e, cy + 22 * e, 12 * e, 5 * e, 0, 0, 7); g.fill() }
  } else if (iM === 2) {                                     // L'USINE : cheminées + fumée
    for (let k = 0; k < 3; k++) {
      const sx = cx + (-26 + k * 24) * e
      g.fillStyle = '#6a6a78'; g.fillRect(sx - 6 * e, cy - 24 * e, 12 * e, 34 * e)
      g.fillStyle = '#8a8a98'; g.fillRect(sx - 7 * e, cy - 27 * e, 14 * e, 4 * e)
      g.fillStyle = rgba('#ffffff', 0.3)
      for (let f = 0; f < 3; f++) { g.beginPath(); g.arc(sx + f * 4 * e, cy + (-34 - f * 8) * e, (4 + f * 2) * e, 0, 7); g.fill() }
    }
  } else if (iM === 3) {                                     // LES MIRAGES : dunes + palmier
    g.fillStyle = rgba('#e0a94a', 0.5)
    for (let k = 0; k < 3; k++) { g.beginPath(); g.ellipse(cx + (-20 + k * 22) * e, cy + 12 * e, 20 * e, 8 * e, 0, Math.PI, 0); g.fill() }
    g.fillStyle = '#7a5a2a'; g.fillRect(cx + 14 * e, cy - 20 * e, 3.4 * e, 24 * e)
    g.fillStyle = '#3f7a52'
    for (const a of [-2.4, -1.9, -1.2, -0.7]) {
      g.beginPath(); g.ellipse(cx + (16 + Math.cos(a) * 10) * e, cy + (-20 + Math.sin(a) * 6) * e, 10 * e, 3.4 * e, a, 0, 7); g.fill()
    }
  } else if (iM === 4) {                                     // LES SERRURES (grotte) : stalagmites
    g.fillStyle = rgba('#6a5a80', 0.8)
    for (let k = 0; k < 5; k++) {
      const sx = cx + (-34 + k * 17) * e, h = (12 + bru(k, 4) * 16) * e
      g.beginPath(); g.moveTo(sx - 5 * e, cy + 14 * e); g.lineTo(sx, cy + 14 * e - h); g.lineTo(sx + 5 * e, cy + 14 * e); g.closePath(); g.fill()
    }
  } else if (iM === 5) {                                     // LA GRANDE FERME : champs + meules
    g.fillStyle = rgba('#8fc45c', 0.45)
    for (let k = 0; k < 4; k++) g.fillRect(cx - 38 * e, cy + (-14 + k * 12) * e, 76 * e, 7 * e)
    g.fillStyle = '#d9b45a'
    for (let k = 0; k < 2; k++) { g.beginPath(); g.arc(cx + (-16 + k * 32) * e, cy - 20 * e, 8 * e, 0, 7); g.fill() }
  } else if (iM === 6) {                                     // LES PROFONDEURS : coraux + bulles
    g.fillStyle = rgba('#57d3c8', 0.55)
    for (let k = 0; k < 4; k++) {
      const sx = cx + (-30 + k * 20) * e
      g.beginPath(); g.moveTo(sx, cy + 16 * e); g.lineTo(sx - 6 * e, cy - 4 * e); g.lineTo(sx, cy + 2 * e)
      g.lineTo(sx + 6 * e, cy - 8 * e); g.closePath(); g.fill()
    }
    g.fillStyle = rgba('#ffffff', 0.35)
    for (let k = 0; k < 5; k++) { g.beginPath(); g.arc(cx + (-24 + k * 13) * e, cy + (-24 - bru(k, 6) * 14) * e, (2 + bru(k, 9) * 2) * e, 0, 7); g.fill() }
  } else {                                                   // LE DERNIER ÉTAGE : le manoir
    g.fillStyle = rgba('#4a3450', 0.85)
    g.fillRect(cx - 26 * e, cy - 22 * e, 52 * e, 34 * e)
    g.beginPath(); g.moveTo(cx - 32 * e, cy - 22 * e); g.lineTo(cx, cy - 42 * e); g.lineTo(cx + 32 * e, cy - 22 * e); g.closePath(); g.fill()
    g.fillStyle = '#ffd23f'
    for (let k = 0; k < 3; k++) g.fillRect(cx + (-16 + k * 14) * e, cy - 12 * e, 8 * e, 9 * e)
  }
  g.restore()
}

/** LA MER, peinte en coordonnées globales sur toute la hauteur de l'archipel puis rognée à la
 *  bande visible. ⚠️ C'est le premier endroit où la couture se voyait : le papier et les vagues
 *  repartaient de zéro à chaque monde, ce qui traçait une ligne nette tous les 400 px. */
function mer(g: Ctx, d: Disposition, haut: number, bas: number): void {
  const total = d.hBande * Math.max(1, d.iles.length)
  g.save()
  g.beginPath(); g.rect(0, haut - 2, d.w, bas - haut + 4); g.clip()
  papier(g, 0, 0, d.w, total, '#2e7ab0', 'chevrons', '#1d5480')
  g.restore()
  g.strokeStyle = rgba('#8fd0f0', 0.3); g.lineWidth = 2.5
  for (let y = Math.max(16, Math.floor((haut - 40) / 34) * 34); y < bas + 40; y += 34) {
    g.beginPath()
    for (let x = -10; x <= d.w + 10; x += 16) {
      const yy = y + Math.sin((x + y) * 0.05) * 3
      x === -10 ? g.moveTo(x, yy) : g.lineTo(x, yy)
    }
    g.stroke()
  }
}

/** Les récifs et le bateau : la mer habitée, en retrait. Placés en coordonnées globales et jamais
 *  sous une île, sinon un récif se retrouverait à moitié sur la plage. */
function large(g: Ctx, d: Disposition, il: Ile): void {
  const sousIle = (x: number, y: number): boolean =>
    d.iles.some((o) => Math.hypot(o.cx - x, (o.cy - y) / 0.72) < o.r * 1.5)
  for (let k = 0; k < 2; k++) {
    const x = (il.gauche ? 0.86 : 0.14) * d.w + (bru(il.rang, 3 + k) - 0.5) * d.w * 0.1
    const y = il.cy + (k === 0 ? -0.3 : 0.34) * d.hBande
    if (sousIle(x, y)) continue
    g.fillStyle = rgba('#1d4a70', 0.7)
    g.beginPath(); g.moveTo(x - 9, y + 4); g.lineTo(x - 3, y - 6); g.lineTo(x + 2, y + 1)
    g.lineTo(x + 7, y - 4); g.lineTo(x + 11, y + 4); g.closePath(); g.fill()
    g.fillStyle = rgba('#ffffff', 0.22)
    g.beginPath(); g.ellipse(x, y + 6, 14, 4, 0, 0, 7); g.fill()
  }
  // un bateau tous les trois mondes : assez pour que la mer vive, pas assez pour qu'elle bavarde
  if (il.rang % 3 !== 1) return
  const bx = (il.gauche ? 0.84 : 0.16) * d.w, by = il.cy + il.r * 0.62
  if (sousIle(bx, by)) return
  g.save(); g.translate(bx, by)
  g.fillStyle = rgba(OMBRE_TON, 0.28); g.beginPath(); g.ellipse(2, 12, 20, 6, 0, 0, 7); g.fill()
  g.fillStyle = '#8a5a2a'
  g.beginPath(); g.moveTo(-18, 4); g.quadraticCurveTo(0, 15, 18, 4); g.closePath(); g.fill()
  g.strokeStyle = '#5a3a20'; g.lineWidth = 2.2
  g.beginPath(); g.moveTo(0, 4); g.lineTo(0, -24); g.stroke()
  g.fillStyle = '#f6efe0'
  g.beginPath(); g.moveTo(2, -22); g.lineTo(16, -4); g.lineTo(2, -4); g.closePath(); g.fill()
  g.beginPath(); g.moveTo(-2, -18); g.lineTo(-13, -4); g.lineTo(-2, -4); g.closePath(); g.fill()
  g.restore()
}

/** L'île : trois couches empilées, chacune avec son ombre. C'est l'empilement qui fait le relief. */
function ile(g: Ctx, il: Ile, accent: string): void {
  const couches: [number, string][] = [[1.0, '#e8cf9a'], [0.82, accent], [0.5, '#f2e4cc']]
  couches.forEach(([k, col], idx) => {
    g.save(); g.translate(0, idx * -5)
    contour(g, il.cx, il.cy, il.r, k, il.cx)
    g.save(); g.translate(4, 7); g.fillStyle = rgba(OMBRE_TON, 0.34); g.fill(); g.restore()
    contour(g, il.cx, il.cy, il.r, k, il.cx)
    g.fillStyle = col; g.fill()
    g.strokeStyle = rgba('#ffffff', 0.28); g.lineWidth = 1.6; g.stroke()
    g.restore()
  })
}

/** ⚠️ LE COMPTE D'ÉTAPES N'EST PLUS PEINT ICI. Il était posé au bas de l'île et se cognait aux
 *  étiquettes des étapes, dont celle du boss, la plus large de toutes. Il vit maintenant dans
 *  l'en-tête DOM du monde (`.pc-etapes`) : toujours lisible, jamais dans le chemin. */
export interface MondeCarte { accent: string }

/**
 * Peint LA CARTE ENTIÈRE dans la fenêtre du monde `rang`, dans un canvas de `disp.w` × `disp.hBande`
 * px CSS. L'appelant a déjà dimensionné le canvas (densité comprise) et posé la transform.
 *
 * ⚠️ On peint toutes les îles qui débordent dans la fenêtre, pas seulement la sienne : c'est ce
 * qui fait que l'archipel se recolle à la jointure au lieu de s'y couper net.
 */
export function peindreBande(
  g: Ctx, rang: number, disp: Disposition, mondes: readonly MondeCarte[],
): void {
  // ⚠️ DETAIL est un état global du module papier : on pose le sien en entrant, on ne suppose
  // jamais celui laissé par le dernier qui a peint.
  setDetail(1)
  const haut = rang * disp.hBande, bas = haut + disp.hBande
  g.save()
  g.translate(0, -haut)
  mer(g, disp, haut, bas)
  const visibles = disp.iles.filter((il) => il.cy + il.r > haut - 60 && il.cy - il.r < bas + 60)
  for (const il of visibles) large(g, disp, il)
  for (const il of visibles) {
    ile(g, il, mondes[il.rang]?.accent ?? '#e8cf9a')
    decor(g, il.cx, il.cy, il.r, il.decor)
  }
  // ── LE RUBAN : un seul chemin, du premier pas au dernier boss. Chaque fenêtre le peint en
  // entier ; le canvas se charge de n'en montrer que sa part, à la virgule près.
  if (disp.ruban.length > 1) {
    const passe = (dx: number, dy: number, col: string, lw: number): void => {
      g.save(); g.translate(dx, dy)
      g.strokeStyle = col; g.lineWidth = lw; g.lineCap = 'round'; g.lineJoin = 'round'
      courbeDouce(g, disp.ruban); g.stroke()
      g.restore()
    }
    passe(3, 5, rgba(OMBRE_TON, 0.32), 11)
    passe(0, 0, '#f2e4cc', 9)
    passe(0, 0, rgba('#c9a878', 0.55), 3)
  }
  g.restore()
}

/** LE JETON d'une étape. Remplace la pastille ronde d'avant : ici l'état se lit à la COULEUR, et
 *  un niveau non joué n'est plus un cadenas répété mais un jeton de papier vierge. */
export function jeton(cv: HTMLCanvasElement, o: { genre: string; etat: string; numero: number }): void {
  const r = o.genre === 'boss' ? 21 : o.genre === 'bonus' ? 14 : 17
  const S = (r + 5) * 2
  const dpr = Math.min(3, (typeof window !== 'undefined' && window.devicePixelRatio) || 1)
  cv.width = Math.round(S * dpr); cv.height = Math.round(S * dpr)
  cv.style.width = S + 'px'; cv.style.height = S + 'px'
  const g = cv.getContext('2d')
  if (!g) return
  g.setTransform(dpr, 0, 0, dpr, 0, 0)
  setDetail(1)
  const c = S / 2
  const fini = o.etat === 'fini', courant = o.etat === 'courant', verrou = o.etat === 'verrouille'
  g.fillStyle = rgba(OMBRE_TON, 0.34)
  g.beginPath(); g.arc(c + 2, c + 4, r, 0, 7); g.fill()
  // ⚠️ Le BOSS avant tout le reste : sinon le boss de l'étape courante virait au jaune et perdait
  // sa signature rouge, la seule chose qui le distingue de loin.
  g.fillStyle = o.genre === 'boss' ? '#e0605a'
    : fini ? '#8fd45c' : courant ? '#ffd23f' : verrou ? '#f6efe0' : '#ffe9a8'
  g.beginPath(); g.arc(c, c, r, 0, 7); g.fill()
  g.strokeStyle = rgba('#ffffff', 0.55); g.lineWidth = 2
  g.beginPath(); g.arc(c, c, r - 1, 0, 7); g.stroke()
  g.textAlign = 'center'; g.textBaseline = 'middle'
  if (o.genre === 'boss') {                                   // deux yeux : la signature des moines
    g.fillStyle = '#2a1a1a'
    g.beginPath(); g.arc(c - 6, c - 1, 3.4, 0, 7); g.arc(c + 6, c - 1, 3.4, 0, 7); g.fill()
  } else if (fini) {
    g.strokeStyle = '#2f5a20'; g.lineWidth = 3.4; g.lineCap = 'round'
    g.beginPath(); g.moveTo(c - 6, c); g.lineTo(c - 1, c + 5); g.lineTo(c + 7, c - 6); g.stroke()
  } else if (verrou) {
    g.fillStyle = rgba('#3a2c1e', 0.35)                       // un jeton vierge, pas un cadenas
    g.beginPath(); g.arc(c, c, 3.4, 0, 7); g.fill()
  } else {
    g.fillStyle = '#3a2c1e'
    g.font = '700 15px ui-monospace, Menlo, monospace'
    g.fillText(String(o.numero), c, c + 1)
  }
}
